import { createGameStore } from '../lib/createGameStore';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type GameStatusState = {
  dragonName: string;
  stunEffectTickDurationRemaining: number;
  playedIntro: boolean;
  playedCoupDeGrace: boolean;
  won: boolean;
  metBigDragon: boolean;
  popeVisited: boolean;
  invasionStarted: boolean;
  invasionEnded: boolean;
  dummyKilled: boolean;
};

export type GameStatusActions = {
  setDragonName: (name: string) => void;
  addStunEffect: (ticks: number) => void;
  tickStunEffect: () => void;

  markIntroPlayed: () => void;
  markCoupDeGracePlayed: () => void;
  markWon: () => void;
  markMetBigDragon: () => void;
  markPopeVisited: () => void;
  markInvasionStarted: () => void;
  markInvasionEnded: () => void;
  markDummyKilled: () => void;

  /** Reset all flags to initial values (e.g. new game). */
  reset: () => void;
};

export type GameStatusStore = GameStatusState & GameStatusActions;

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

const initialGameStatusState: GameStatusState = {
  dragonName: 'smallDragon',
  stunEffectTickDurationRemaining: 0,
  playedIntro: false,
  playedCoupDeGrace: false,
  won: false,
  metBigDragon: false,
  popeVisited: false,
  invasionStarted: false,
  invasionEnded: false,
  dummyKilled: false,
};

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

/**
 * Tracks narrative flags and one-time game events.
 *
 * React usage:
 *   const won = useGameStatusStore(s => s.won);
 *
 * Excalibur / outside React:
 *   const { won } = useGameStatusStore.getState();
 *   useGameStatusStore.subscribe(s => s.won, won => { if (won) showWinScreen(); });
 */
export const useGameStatusStore = createGameStore<GameStatusStore>(
  { saveKey: 'game-status' },
  (set, get) => ({
    ...initialGameStatusState,

    setDragonName: (dragonName) => set({ dragonName }),

    addStunEffect: (ticks) =>
      set({
        stunEffectTickDurationRemaining:
          get().stunEffectTickDurationRemaining + ticks,
      }),

    tickStunEffect: () => {
      const remaining = get().stunEffectTickDurationRemaining;
      if (remaining > 0) {
        set({ stunEffectTickDurationRemaining: remaining - 1 });
      }
    },

    markIntroPlayed: () => set({ playedIntro: true }),
    markCoupDeGracePlayed: () => set({ playedCoupDeGrace: true }),
    markWon: () => set({ won: true }),
    markMetBigDragon: () => set({ metBigDragon: true }),
    markPopeVisited: () => set({ popeVisited: true }),
    markInvasionStarted: () => set({ invasionStarted: true }),
    markInvasionEnded: () => set({ invasionEnded: true }),
    markDummyKilled: () => set({ dummyKilled: true }),

    reset: () => set(initialGameStatusState),
  }),
);
